import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function Iso27001VsSoc2Page() {
  const faqs = [
    {
      question: 'Should I pursue SOC 2 or ISO 27001 first?',
      answer:
        'The answer depends on your market. If your customers are primarily US enterprises or US-based SaaS buyers, SOC 2 is the expected standard and should come first. If you are selling to European enterprises, government contracts, or global enterprise accounts, ISO 27001 is often required and carries more weight internationally. Many teams pursue both simultaneously because the GitHub evidence overlaps by 60–70%, meaning the cost of pursuing the second framework is significantly lower once the first is in place.',
    },
    {
      question: 'What evidence is shared between SOC 2 and ISO 27001?',
      answer:
        'GitHub change control evidence satisfies both frameworks. Pull request approval records address SOC 2 CC8.1 and ISO 27001 A.8.32. Branch protection configuration and drift records address SOC 2 CC6.1 and ISO 27001 A.8.32. CI check results address SOC 2 CC8.1 testing requirements and ISO 27001 A.8.32 testing requirements. Exception records address both frameworks\' emergency change documentation requirements. EvidentTrail captures this evidence once and it serves both audit processes.',
    },
    {
      question: 'Is ISO 27001 harder to achieve than SOC 2?',
      answer:
        'ISO 27001 is generally considered more comprehensive — it covers 93 controls across 11 domains compared to SOC 2\'s Trust Services Criteria, which focus more narrowly on the five trust service categories (Security, Availability, Processing Integrity, Confidentiality, Privacy). ISO 27001 requires establishing an Information Security Management System (ISMS) with ongoing management commitment. SOC 2 is more audit-focused — it assesses controls against a defined period. For GitHub change control specifically, the evidence requirements are substantially equivalent.',
    },
    {
      question: 'Does ISO 27001 certification expire?',
      answer:
        'ISO 27001 certificates are valid for three years, with annual surveillance audits in years one and two. At the end of the three-year cycle, a recertification audit is required. This means continuous evidence collection throughout the certificate period is important — the surveillance audits in years one and two will sample evidence from the period since the last audit.',
    },
    {
      question: 'Can the same GitHub evidence pack be used for both SOC 2 and ISO 27001 audits?',
      answer:
        'Yes. EvidentTrail evidence packs contain the same underlying data — PR approvals, CI results, branch protection snapshots, exception records — which maps to both CC8.1/CC6.1 (SOC 2) and A.8.32 (ISO 27001). The presentation may differ slightly for each auditor, but the evidence artifacts are the same. You can generate separate scoped packs for each audit period or share the same pack with different auditors.',
    },
  ];

  const longformSections = [
    {
      title: 'What each standard actually measures',
      paragraphs: [
        'SOC 2 is an auditing standard created by the American Institute of Certified Public Accountants (AICPA). It assesses whether a service organization\'s controls meet the Trust Services Criteria across five categories: Security (required), Availability, Processing Integrity, Confidentiality, and Privacy. A SOC 2 report is issued by a licensed CPA firm and is the dominant compliance credential for US SaaS companies.',
        'ISO 27001 is an international standard published by the International Organization for Standardization (ISO). It defines requirements for establishing, implementing, maintaining, and continually improving an Information Security Management System (ISMS). Certification is issued by an accredited certification body and is recognized globally — particularly in Europe, APAC, and government procurement contexts.',
        'For software development teams, both frameworks require essentially the same things: documented change management processes, access controls on code repositories, evidence that changes were reviewed and authorized before deployment, and monitoring for unauthorized changes. The difference is in scope, market recognition, and the depth of the management system required.',
      ],
      bullets: [
        'SOC 2: US-focused, CPA-audited, report-based, primary credential for US SaaS',
        'ISO 27001: International, accredited certification, ISMS-based, required for EU/APAC enterprise',
        'Change management: both require documented, reviewed, authorized changes',
        'Access control: both require controlled access to production systems and code',
      ],
    },
    {
      title: 'GitHub evidence overlap: 60–70% shared',
      paragraphs: [
        'The practical implication of the framework overlap is that your GitHub change control evidence serves both audits with minimal duplication of effort. Pull request approval records address SOC 2 CC8.1 change authorization and ISO 27001 A.8.32 change authorization using the same underlying data.',
        'Branch protection configuration addresses SOC 2 CC6.1 logical access controls and ISO 27001 A.8.32 change control simultaneously — the same snapshot shows that only authorized individuals could approve merges. CI check results address both frameworks\' requirements for testing before deployment. Exception records for emergency changes satisfy both frameworks\' requirements for documented deviations from normal process.',
        'The 30–40% that does not overlap tends to be at the management system level — ISO 27001 requires a documented ISMS policy, a risk assessment methodology, management review, and an internal audit program that SOC 2 does not explicitly require. At the technical evidence level, particularly for GitHub-based software teams, the overlap is close to 70%.',
      ],
      bullets: [
        'PR approval records: satisfies CC8.1 (SOC 2) and A.8.32 (ISO 27001)',
        'Branch protection snapshots: satisfies CC6.1 (SOC 2) and A.8.32 (ISO 27001)',
        'CI check results: satisfies both frameworks\' testing requirements',
        'Exception records: satisfies both frameworks\' emergency change documentation',
        'Non-overlapping: ISO 27001 ISMS documentation, risk register, management reviews',
      ],
    },
    {
      title: 'Timing: which to pursue first',
      paragraphs: [
        'For most early-stage software companies with US customers, SOC 2 Type II comes first. It is faster to complete (6 months of evidence collection plus a 4–8 week audit), recognized by US buyers, and generates the evidence infrastructure that makes ISO 27001 significantly easier to add later.',
        'For companies with European customers or government contracts, ISO 27001 may be the first requirement. If a European enterprise customer requires ISO 27001 certification before signing, that determines the sequence. In this case, pursuing both simultaneously from the start is often the most efficient path — the ISMS documentation effort can be done alongside the SOC 2 evidence period.',
        'The optimal timing for EvidentTrail is to install at the start of the SOC 2 evidence period. This ensures you have full-period continuous capture for both the SOC 2 audit and the ISO 27001 assessment — with the same evidence pack serving both.',
      ],
      bullets: [
        'US SaaS: SOC 2 first (6-month evidence period + 4–8 week audit)',
        'EU/government: ISO 27001 first or simultaneous if enterprise contract requires it',
        'Simultaneous: efficient because evidence overlaps — one collection serves both',
        'Start EvidentTrail at the beginning of the evidence period — not before the audit',
      ],
    },
    {
      title: 'Cost and timeline comparison',
      paragraphs: [
        'SOC 2 Type II typically costs $20,000–$60,000 for the audit itself (CPA firm fees), plus internal time for evidence preparation — often 100–200 hours for teams doing it manually. With automated evidence collection, the internal time drops to 10–20 hours per audit cycle. The evidence period is 6–12 months.',
        'ISO 27001 certification typically costs $15,000–$40,000 for the certification audit plus internal effort for building the ISMS — documentation, risk assessment, management review, and internal audit. This internal effort is larger than SOC 2 because ISO 27001 requires a management system, not just an audit. The evidence period is ongoing — surveillance audits happen annually.',
        'Pursuing both simultaneously adds roughly 30–40% to the total cost compared to either alone, but significantly less than doing them sequentially because of the shared evidence infrastructure. Once both are in place, renewal costs drop substantially because the evidence systems are already running.',
      ],
      bullets: [
        'SOC 2 Type II audit: $20K–$60K (CPA firm) + 100–200 hours internal (manual)',
        'SOC 2 with EvidentTrail: same audit cost, 10–20 hours internal',
        'ISO 27001: $15K–$40K (certification body) + ISMS documentation effort',
        'Both simultaneously: ~30–40% premium over either alone, less than sequential',
      ],
    },
  ];

  return (
    <MarketingLayout
      title="ISO 27001 vs SOC 2: GitHub Evidence Comparison | EvidentTrail"
      description="Compare ISO 27001 A.8.32 and SOC 2 CC8.1 change management requirements. Learn which framework to pursue first, what GitHub evidence satisfies both, and how to avoid duplicate effort."
      h1="ISO 27001 vs SOC 2: Which Framework and What Evidence?"
      heroText="Both ISO 27001 and SOC 2 require GitHub change control evidence. The same pull request approvals, branch protection snapshots, and CI records satisfy both — here is how they compare and how to pursue them efficiently."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'ISO 27001 vs SOC 2', url: 'https://evidenttrail.com/iso-27001-vs-soc2' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">

            {/* Quick comparison table */}
            <h2 className={`${tokens.heading.h2} mb-6`}>Side-by-Side Comparison</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium w-1/3">Aspect</th>
                    <th className="text-left py-3 px-4 text-blue-400 font-medium w-1/3">ISO 27001</th>
                    <th className="text-left py-3 px-4 text-emerald-400 font-medium w-1/3">SOC 2</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {[
                    ['Standard body', 'ISO/IEC (international)', 'AICPA (US)'],
                    ['Geographic recognition', 'Global — EU, APAC, UK, worldwide', 'Primarily US market'],
                    ['Output', 'Certificate (3-year validity)', 'Audit report (annual)'],
                    ['Auditor type', 'Accredited certification body', 'Licensed CPA firm'],
                    ['Change control control', 'Annex A 8.32', 'CC8.1'],
                    ['Access control', 'Annex A 8.3, 8.5', 'CC6.1'],
                    ['Evidence period', 'Ongoing (annual surveillance)', '6–12 months (Type II)'],
                    ['GitHub evidence overlap', '~65% shared with SOC 2', '~65% shared with ISO 27001'],
                    ['Management system required', 'Yes — full ISMS', 'No — controls-focused'],
                  ].map(([aspect, iso, soc2]) => (
                    <tr key={aspect} className="hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-300 font-medium">{aspect}</td>
                      <td className="py-3 px-4 text-slate-400">{iso}</td>
                      <td className="py-3 px-4 text-slate-400">{soc2}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Shared evidence callout */}
            <div className="mb-12 p-6 bg-primary-900/20 border border-primary-400/30 rounded-xl">
              <h3 className="text-white font-bold mb-3">Evidence that satisfies both frameworks</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  ['PR approval records', 'CC8.1 + A.8.32'],
                  ['Branch protection snapshots', 'CC6.1 + A.8.32'],
                  ['CI check results', 'CC8.1 + A.8.32'],
                  ['Exception records', 'CC8.1 + A.8.32'],
                  ['Drift detection records', 'CC7.1 + A.8.32'],
                  ['Reviewer identity + timestamps', 'CC8.1 + A.8.32'],
                ].map(([evidence, controls]) => (
                  <div key={evidence} className="flex justify-between items-center py-1">
                    <span className="text-slate-300">{evidence}</span>
                    <span className="text-primary-400 text-xs font-mono">{controls}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-400 text-sm mt-4">
                EvidentTrail captures this evidence once — the same pack serves both auditors.
              </p>
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start Collecting Evidence for Both
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Explore by Framework</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/soc2-change-management-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 CC8.1 Evidence</h4>
              <p className="text-sm text-slate-400">CC8.1 and CC6.1 evidence artifacts, Type II continuous capture.</p>
            </Link>
            <Link to="/iso-27001-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 GitHub Guide</h4>
              <p className="text-sm text-slate-400">Complete GitHub configuration and evidence guide for ISO 27001 A.8.32 audits.</p>
            </Link>
            <Link to="/solutions/iso-27001-a832-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 A.8.32 Deep Dive</h4>
              <p className="text-sm text-slate-400">Control-level mapping of GitHub evidence to A.8.32 requirements.</p>
            </Link>
            <Link to="/soc2-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 GitHub Guide</h4>
              <p className="text-sm text-slate-400">Complete GitHub configuration checklist for SOC 2 compliance.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
